import { Theme, ThemeProvider } from '@emotion/react';
import { IStore } from '@snackpass/snackpass-types';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import store, { RootState } from './modules/Redux/store';
import ErrorBoundaryFallback from './ui/pages/ErrorBoundaryFallback';
import Routing from './ui/pages/Routing';
import { DarkColors, LightColors } from './ui/theme';
import './ui/theme/emotion.d';
import { AudioControllerProvider } from './ui/pages/CustomerPickupScreen/hooks/AudioControlContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

function observeStore<T, U>(
    store: Store,
    select: (state: RootState) => T,
    onChange: (state: T) => U
) {
    let currentState: T;

    function handleChange() {
        let nextState = select(store.getState());
        if (nextState !== currentState) {
            currentState = nextState;
            onChange(currentState);
        }
    }

    let unsubscribe = store.subscribe(handleChange);
    handleChange();
    return unsubscribe;
}

const queryClient = new QueryClient();

function App() {
    /** state */
    const isDark = false; // If Light / Dark is available, useState instead
    const [activeStore, setActiveStore] = useState<IStore | null>(null);

    /** state */

    const activeStoreSelector = (state: RootState) => state.stores.activeStore;

    useEffect(() => {
        const unsubscribe = observeStore(
            store,
            activeStoreSelector,
            _activeStore => setActiveStore(_activeStore)
        );

        return unsubscribe;
    }, []);

    const adjustedTheme = (ancestorTheme: Theme) => ({
        ...ancestorTheme,
        primary: activeStore?.brandColor || ancestorTheme.primary,
    });

    /** Error handler for logging errors during development. `Sentry.ErrorBoundary` handles logging to Sentry. */
    const onError = useCallback((error: Error) => {
        console.error(`Error Boundary Hit: ${error.message}`);
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <Sentry.ErrorBoundary
                    fallback={<ErrorBoundaryFallback />}
                    onError={onError}
                >
                    <ThemeProvider theme={isDark ? DarkColors : LightColors}>
                        <ThemeProvider theme={adjustedTheme}>
                            <AudioControllerProvider>
                                <Routing />
                            </AudioControllerProvider>
                        </ThemeProvider>
                    </ThemeProvider>
                </Sentry.ErrorBoundary>
            </Provider>
        </QueryClientProvider>
    );
}

export default App;
